<template>
    <v-row v-if="message_patterns">
        <v-col cols="12">
            <div class="review-item py-0">
                <div class="d-flex align-center gap-1 mb-1">
                    <div class="review-label d-flex flex-wrap align-center justify-space-between flex-grow-1 gap-2 mb-1">
                        <div class="d-flex align-center">
                            <v-icon
                                color="primary"
                                aria-hidden="true"
                            >
                                mdi-message-text-outline
                            </v-icon>
                            <div class="d-flex gap-2 align-center ml-2">
                                <h2>Message Content</h2>
                                <span
                                    v-if="message_patterns.length > 1"
                                    class="text-caption text--secondary d-inline-block"
                                >
                                    ({{ message_patterns.length }} patterns)
                                </span>
                            </div>
                        </div>

                        <div class="d-flex flex-wrap align-center gap-2">
                            <v-chip
                                v-if="from_endpoint_label"
                                class="text-capitalize"
                                color="info"
                                outlined
                                label
                                small
                            >
                                <v-icon
                                    color="info"
                                    small
                                    left
                                >
                                    mdi-account
                                </v-icon>
                                From: {{ from_endpoint_label }}
                            </v-chip>

                            <v-menu
                                v-if="attachment_total_count > 0"
                                offset-y
                                :close-on-content-click="false"
                                max-width="400"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-chip
                                        small
                                        label
                                        outlined
                                        color="info"
                                        class="attachments-chip"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon
                                            small
                                            left
                                            color="info"
                                        >
                                            mdi-paperclip
                                        </v-icon>
                                        <span class="info--text">
                                            {{ attachment_total_count }} Attachment{{ attachment_total_count > 1 ? 's' :
                                                '' }}
                                        </span>
                                        <v-icon
                                            small
                                            right
                                            color="info"
                                        >
                                            mdi-menu-down
                                        </v-icon>
                                    </v-chip>
                                </template>

                                <v-card class="attachments-preview-card">
                                    <v-card-title class="text-subtitle-2 py-2 px-3">
                                        Attachments ({{ attachment_total_count }})
                                    </v-card-title>
                                    <v-divider />
                                    <v-card-text class="pa-3">
                                        <div class="d-flex flex-wrap gap-3">
                                            <message-media-preview
                                                v-if="blob_meta_data.length"
                                                :attachment="blob_meta_data[0]"
                                                :allow_edit="false"
                                            />

                                            <message-media-preview
                                                v-if="bulk_message.vcard_id"
                                                :contact_card_id="bulk_message.vcard_id"
                                                :allow_edit="false"
                                            />
                                        </div>

                                        <p
                                            v-if="include_pre_send_disclaimer"
                                            class="text-caption text--secondary mt-3 mb-0"
                                        >
                                            <strong>Note:</strong> RedChirp will attempt to send the attachment{{
                                                attachment_total_count > 1 ? 's' : ''
                                            }} above to all recipients. To save space, the recipients table below will
                                            not include the attachment{{
                                                attachment_total_count > 1 ? 's' : '' }} in the Message column.
                                        </p>
                                    </v-card-text>
                                </v-card>
                            </v-menu>

                            <v-chip
                                v-else
                                small
                                label
                                outlined
                                color="info"
                            >
                                <v-icon
                                    small
                                    left
                                >
                                    mdi-paperclip-off
                                </v-icon>
                                No Attachments
                            </v-chip>
                        </div>
                    </div>
                </div>

                <div
                    v-if="message_patterns.length === 1"
                    class="review-value message-preview"
                    v-html="$rc.wrapHyperlinks(message_patterns[0])"
                >
                </div>

                <div
                    v-else-if="message_patterns.length > 1"
                    class="review-value"
                >
                    <v-expansion-panels
                        class="message-patterns-expansion"
                        v-model="message_patterns_expanded"
                        multiple
                        flat
                    >
                        <v-expansion-panel
                            v-for="(pattern, index) in message_patterns"
                            :key="index"
                        >
                            <v-expansion-panel-header class="pattern-header">
                                <span class="font-weight-medium">Pattern {{ index + 1 }}</span>
                                <span class="text-caption text--secondary ml-2">
                                    ({{ pattern.length }} characters)
                                </span>
                            </v-expansion-panel-header>

                            <v-expansion-panel-content>
                                <div class="message-preview" v-html="$rc.wrapHyperlinks(pattern)"></div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>

                <div
                    v-else
                    class="review-value text--secondary"
                >
                    No message content selected
                </div>
            </div>
        </v-col>
    </v-row>
</template>

<script>
import E164 from '@/js/E164.js';
import MessageMediaPreview from '@/components/bulk/message-media-preview.vue';

export default {
    name: 'MessagePatternsReadonly',

    components: {
        MessageMediaPreview,
    },

    props: {
        bulk_message: {
            type: Object,
            required: true
        },
        include_pre_send_disclaimer: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            blob_meta_data: [],
            from_endpoint_label: null,
            message_patterns_expanded: [0],
        };
    },

    computed: {
        message_patterns() {
            return this.bulk_message?.message_patterns || [];
        },

        attachment_total_count() {
            let count = 0;
            if (this.bulk_message?.blob_ids) {
                count += this.bulk_message.blob_ids.length;
            }
            if (this.bulk_message?.vcard_id) {
                count += 1;
            }
            return count;
        },
    },

    methods: {
        loadBlobMetadata() {
            if (!this.bulk_message?.blob_ids || this.bulk_message.blob_ids.length === 0) return;

            this.$rc.getBlobMetaDataMultiple(
                this.bulk_message.blob_ids,

                (data) => {
                    this.blob_meta_data = data.meta_data;
                },
                () => {
                    this.$rc.alert('Error', 'Unable to get image information');
                }
            );
        },

        loadFromEndpointLabel() {
            if (!this.bulk_message?.from_endpoint) {
                return;
            }

            // exclude bot numbers
            const params = {
                assignment: {
                    type: "!=",
                    value: ['assigned-to-organization-bot', 'available'],
                },
            };

            this.$rc.rest({
                spinner: false,
                endpoint: 'LIST: /api/v1/numbers',
                params,

                on_success: (data) => {
                    const phone_number = data.results.find(
                        (num) => num.endpoint === this.bulk_message.from_endpoint
                    );

                    if (phone_number) {
                        this.from_endpoint_label = `${E164.getPhoneNumberLabel(phone_number)} - ${E164.prettyPrintUsPhoneNumber(phone_number.endpoint)}`;
                    } else {
                        this.from_endpoint_label = `${this.bulk_message.from_endpoint} (Unavailable)`;
                    }
                },

                on_error: () => {
                    // no-op, display is progressive enhancement
                },
            });
        },
    },

    mounted() {
        this.loadBlobMetadata();
        this.loadFromEndpointLabel();
    },
};
</script>

<style lang="scss" scoped>
.review-item {
    margin-bottom: 0.75rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);

    &:last-child {
        border-bottom: none;
    }
}

.review-label {
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 0.33rem;
}

.review-value {
    font-size: 1rem;
    line-height: 1.4;
}

.message-preview {
    background-color: var(--color-bg-shaded-light, #f5f5f5);
    padding: 12px;
    border-radius: 6px;
    font-family: 'Roboto Mono', monospace;
    font-size: 13px;
    line-height: 1.4;
    color: var(--v-secondary-darken3);
    /* white-space: pre-wrap results in added white space prepended */
    white-space: pre-line;
    word-break: break-word;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.message-patterns-expansion {
    row-gap: 0.5rem;

    .v-expansion-panel {
        margin: 0 !important;

        :deep(.v-expansion-panel-content__wrap) {
            padding: 0.75rem 0.5rem 0.25rem 0.5rem;
        }
    }

    .pattern-header {
        min-height: 40px !important;
        border: 1px solid #E0E0E0;
        border-radius: 4px;

        .v-expansion-panel-header__icon {
            margin-left: auto;
        }

        &>* {
            flex-grow: 0;
        }
    }
}

.attachments-chip {
    cursor: pointer;
}

.attachments-preview-card {
    max-width: 400px;
}

@media (max-width: 600px) {
    .review-item {
        padding: 12px 0;
    }

    .review-label {
        font-size: 0.8rem;
    }

    .review-value {
        font-size: 0.95rem;
    }
}
</style>